import { loadMessages, LocalizationProvider } from '@progress/kendo-react-intl';
import PropTypes from 'prop-types';
import { memo, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import {
  TrlCheckButton,
  TrlLoadingIndicator,
  TrlPdfViewer,
} from '»components/atoms';
import { ButtonMain } from '»components/atoms/Buttons';
import { disclaimersServices } from '»components/molecules/TrlDisclaimer/trlDisclaimer.service';
import { useHandleSagaError } from '»utils/customHooks';
import { getFileBase64 } from '»utils/files';

import { logoutSlice } from '../Logout';
import { areGenCondAccepted, isAuthenticatedSelector } from '../redux';
import { generalConditionsServices } from './generalConditions.services';
import { generalConditionsSlice } from './generalConditions.slice';
import {
  ButtonsContainer,
  CheckboxContainer,
  CheckboxText,
  DialogStyled,
  OutsidePdfContainer,
  PaperStyled,
  TitleStyled,
} from './GeneralConditions.styled';
import ptMessages from './pt.json';

loadMessages(ptMessages, 'pt');
export const GeneralConditions = memo(() => {
  const dispatch = useDispatch();

  const [checked, setChecked] = useState('N');
  const [pdf, setPdf] = useState(null);
  const [isRequestingPdf, setIsRequestingPdf] = useState(false);
  const [isRequestingDisclaimer, setIsRequestingDisclaimer] = useState(false);
  const [documentInfo, setDocumentInfo] = useState(null);
  const [isScrollAtBottom, setIsScrollAtBottom] = useState(false);
  const [acceptCondLabel, setAcceptCondLabel] = useState('');
  const [isSinglePagePdf, setIsSinglePagePdf] = useState(false);

  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const generalCond = useSelector(areGenCondAccepted);

  const { handleSagaError } = useHandleSagaError();

  const pdfRef = useRef(null);

  const showModal = isAuthenticated && generalCond === 'N';

  const intl = useIntl();

  const locale = intl.locale.includes('pt') ? 'pt' : 'en';

  const handleCheckAgreement = (event) => {
    if (event.target.checked) {
      setChecked('Y');
    } else {
      setChecked('N');
    }
  };

  const handleSubmit = () => {
    const { documentId, documentVersion } = documentInfo;

    dispatch(
      generalConditionsSlice.actions.setGeneralConditionsRequest({
        documentId,
        documentVersion,
      }),
    );
  };

  const handleClose = () => {
    dispatch(logoutSlice.actions.logoutRequest({ showAlert: false }));
  };

  const detectScrolledToBottom = (ev) => {
    const atBottom =
      Math.abs(
        ev.target.scrollHeight - ev.target.scrollTop - ev.target.clientHeight,
      ) < 1;

    if (atBottom) setIsScrollAtBottom(true);
  };

  useEffect(() => {
    if (showModal) {
      getPdfFile();
      getDisclaimer();
    }

    return () => {
      setChecked('N');
      setIsScrollAtBottom(false);
      setIsSinglePagePdf(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  useEffect(() => {
    const scrollableContainer = pdfRef.current?.element.querySelector(
      '.k-pdf-viewer-canvas',
    );

    scrollableContainer?.addEventListener('scroll', detectScrolledToBottom);

    return () => {
      scrollableContainer?.removeEventListener(
        'scroll',
        detectScrolledToBottom,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfRef.current]);

  const getPdfFile = () => {
    setIsRequestingPdf(true);
    generalConditionsServices
      .getFile()
      .then((response) => {
        const { documentId, documentVersion, documentHeader } = response[0];

        setDocumentInfo({ documentId, documentVersion, documentHeader });

        generalConditionsServices
          .downloadFile({ documentId, documentVersion })
          .then((res) => {
            setIsRequestingPdf(false);
            const fileContent = res.data;

            getFileBase64(fileContent, setPdf);
          })
          .catch((error) => {
            handleSagaError(error);
            setIsRequestingPdf(false);
          });
      })
      .catch((error) => {
        handleSagaError(error);
        setIsRequestingPdf(false);
      });
  };

  const getDisclaimer = () => {
    setIsRequestingDisclaimer(true);
    disclaimersServices
      .getDisclaimer({
        context: 'COND_GERAIS',
      })
      .then((disclaimerRes) => {
        setIsRequestingDisclaimer(false);
        setAcceptCondLabel(disclaimerRes[0].checkboxNote);
      })
      .catch((error) => {
        handleSagaError(error);
        setIsRequestingDisclaimer(false);
      });
  };

  const isAgreementCheckboxEnabled = isSinglePagePdf || isScrollAtBottom;

  return (
    <DialogStyled
      open={showModal}
      onClose={handleClose}
      PaperComponent={PaperStyled}
    >
      {(isRequestingPdf || isRequestingDisclaimer) && <TrlLoadingIndicator />}
      <OutsidePdfContainer>
        <TitleStyled>{documentInfo?.documentHeader}</TitleStyled>
      </OutsidePdfContainer>
      <LocalizationProvider language={locale}>
        <TrlPdfViewer
          pdf={pdf}
          defaultZoom={0.5}
          tools={['pager', 'spacer', 'zoomInOut', 'spacer', 'download']}
          setIsScrollAtBottom={setIsScrollAtBottom}
          setIsSinglePagePdf={setIsSinglePagePdf}
        />
      </LocalizationProvider>
      <OutsidePdfContainer>
        <CheckboxContainer>
          <TrlCheckButton
            value={checked}
            handleChange={handleCheckAgreement}
            disabled={!isAgreementCheckboxEnabled}
          />
          <CheckboxText disabled={!isAgreementCheckboxEnabled}>
            {acceptCondLabel}
          </CheckboxText>
        </CheckboxContainer>
        <ButtonsContainer>
          <ButtonMain
            onClick={handleClose}
            expressionId="TRLT_UI-CANCEL"
            mr={2}
          />
          <ButtonMain
            disabled={checked === 'N'}
            onClick={handleSubmit}
            expressionId="TRLT_UI-NEXT"
            type="submit"
            id="submit"
          />
        </ButtonsContainer>
      </OutsidePdfContainer>
    </DialogStyled>
  );
});

GeneralConditions.displayName = 'GeneralConditions';

GeneralConditions.propTypes = {
  isAuthenticated: PropTypes.bool,
  generalCond: PropTypes.oneOf(['Y', 'N']),
};
